import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'react-apollo';
import { get } from 'lodash';
import useCurrentUser from './useCurrentUser';
import personQuickLinksGql from './data/personQuickLinks.graphql';
import personQuickLinksCountGql from './data/personQuickLinksCount.graphql';
import deletePersonQuickLinkGql from './data/deletePersonQuickLink.graphql';
import updatePersonQuickLinkSequenceGql from './data/updatePersonQuickLinkSequence.graphql';
import getGqlOperationName from '../../utils/getGqlOperationName';
import useQuickLinksContext from '../../components/containers/QuickLinksContextProvider/useQuickLinksContext';
import createPersonQuickLinkGql from '../../components/forms/QuickLinkModal/data/createPersonQuickLink.graphql';
import updatePersonQuickLinkGql from '../../components/forms/QuickLinkModal/data/updatePersonQuickLink.graphql';
const COUNT_STEP = 20;
const useQuickLinks = ({ countStep = COUNT_STEP, searchQuery, } = {}) => {
    const { me: { preferences: { QUICK_LINK_SETTINGS }, }, } = useCurrentUser();
    const { links: contextLinks, setLinks, count: contextCount, setLinksCount, } = useQuickLinksContext();
    const [count, setCount] = useState(countStep);
    useEffect(() => {
        setCount(countStep);
    }, [countStep]);
    const refetchQueries = useMemo(() => [
        {
            query: personQuickLinksGql,
            variables: { count, searchQuery },
        },
    ], [count, searchQuery]);
    const { data, loading, error, refetch } = useQuery(personQuickLinksGql, {
        variables: { count, searchQuery },
        fetchPolicy: 'network-only',
    });
    const [deletePersonQuickLinkMutation] = useMutation(deletePersonQuickLinkGql);
    const [createPersonQuickLinkMutation] = useMutation(createPersonQuickLinkGql);
    const [updatePersonQuickLinkMutation] = useMutation(updatePersonQuickLinkGql);
    const [updatePersonQuickLinkSequenceMutation] = useMutation(updatePersonQuickLinkSequenceGql);
    const deletePersonQuickLink = useCallback(variables => deletePersonQuickLinkMutation({
        variables,
        refetchQueries,
        fetchPolicy: 'no-cache',
    }), [deletePersonQuickLinkMutation, refetchQueries]);
    const updatePersonQuickLinkSequence = useCallback(params => updatePersonQuickLinkSequenceMutation({
        variables: { params },
        refetchQueries,
        fetchPolicy: 'no-cache',
    }), [updatePersonQuickLinkSequenceMutation, refetchQueries]);
    const createPersonQuickLink = useCallback(params => createPersonQuickLinkMutation({
        variables: { params },
        refetchQueries,
        awaitRefetchQueries: true,
    }), [createPersonQuickLinkMutation, refetchQueries]);
    const updatePersonQuickLink = useCallback(params => updatePersonQuickLinkMutation({
        variables: { params },
        refetchQueries,
    }), [updatePersonQuickLinkMutation, refetchQueries]);
    const handleMore = useCallback(e => {
        e.preventDefault();
        setCount(count + countStep);
    }, [setCount, count, countStep]);
    const totalCount = useMemo(() => get(data, getGqlOperationName(personQuickLinksCountGql), 0), [data]);
    const links = useMemo(() => get(data, getGqlOperationName(personQuickLinksGql), []), [data]);
    useEffect(() => {
        setLinks(links);
        setLinksCount(totalCount);
    }, [links, setLinks, totalCount, setLinksCount]);
    return {
        preferences: QUICK_LINK_SETTINGS,
        data: {
            links: contextLinks,
            totalCount: contextCount,
        },
        loading: {
            data: loading,
        },
        errors: {
            data: error,
        },
        mutations: {
            deletePersonQuickLink,
            createPersonQuickLink,
            updatePersonQuickLink,
            updatePersonQuickLinkSequence,
        },
        handleMore,
        state: {
            count,
        },
        gql: {
            list: personQuickLinksGql,
            count: personQuickLinksCountGql,
            refetch,
        },
    };
};
export default useQuickLinks;
